import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default notification timing settings (will be overridden by API)
const DEFAULT_MIN_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const DEFAULT_INITIAL_DELAY_MS = 30 * 1000; // 30 seconds
const ACTIVITY_CHECK_INTERVAL_MS = 30 * 1000; // Check activity every 30 seconds
const INACTIVITY_THRESHOLD_MS = 60 * 1000; // Consider inactive after 1 minute

// Storage key for last notification time
const LAST_NOTIFICATION_STORAGE_KEY = 'calmguard_last_notification_time';

// Helper to get last notification time from storage
const getLastNotificationTime = () => {
  const stored = localStorage.getItem(LAST_NOTIFICATION_STORAGE_KEY);
  return stored ? parseInt(stored, 10) : 0;
};

// Helper to set last notification time in storage
const setLastNotificationTime = (time) => {
  localStorage.setItem(LAST_NOTIFICATION_STORAGE_KEY, time.toString());
};

// Helper to clear notification timer (called on logout)
export const clearNotificationTimer = () => {
  localStorage.removeItem(LAST_NOTIFICATION_STORAGE_KEY);
};

export default function NotificationCardManager({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, getToken } = useAuth();
  
  const [currentCard, setCurrentCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showTurnOffMessage, setShowTurnOffMessage] = useState(false);
  const [timingSettings, setTimingSettings] = useState({
    minIntervalMs: DEFAULT_MIN_INTERVAL_MS,
    initialDelayMs: DEFAULT_INITIAL_DELAY_MS
  });
  
  const lastNotificationTime = useRef(getLastNotificationTime());
  const lastActivityTime = useRef(0);
  const checkIntervalRef = useRef(null);
  const turnOffMessageTimeoutRef = useRef(null);

  // Initialize lastActivityTime on mount
  useEffect(() => {
    lastActivityTime.current = Date.now();
  }, []);

  // Pages where notifications should NOT appear
  const excludedPaths = ['/login', '/register', '/onboarding', '/'];

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityTime.current = Date.now();
    };

    window.addEventListener('click', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, []);

  // Check if user is active
  const isUserActive = useCallback(() => {
    return Date.now() - lastActivityTime.current < INACTIVITY_THRESHOLD_MS;
  }, []);

  // Check if another modal is open
  const isOtherModalOpen = useCallback(() => {
    // Check for common modal indicators
    const modals = document.querySelectorAll('[role="dialog"], [data-modal="true"], .modal-overlay');
    return modals.length > 0;
  }, []);

  // Fetch a random card
  const fetchRandomCard = useCallback(async () => {
    if (!currentUser) return null;
    
    try {
      const token = await getToken();
      const response = await axios.get(`${BACKEND_URL}/api/notification-cards/random`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update timing settings from API response
      if (response.data.timing) {
        setTimingSettings({
          minIntervalMs: (response.data.timing.minIntervalMinutes || 10) * 60 * 1000,
          initialDelayMs: (response.data.timing.initialDelaySeconds || 30) * 1000
        });
      }
      
      if (response.data.card) {
        return response.data.card;
      }
      
      if (response.data.reason === 'notifications_disabled') {
        setNotificationsEnabled(false);
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching notification card:', error);
      return null;
    }
  }, [currentUser, getToken]);

  // Try to show a notification
  const tryShowNotification = useCallback(async () => {
    // Don't show if:
    // - Not logged in
    // - On excluded path
    // - Already showing a notification
    // - Notifications disabled
    // - Another modal is open
    // - User is inactive
    // - Too soon since last notification
    
    if (!currentUser) return;
    if (excludedPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) return;
    if (isVisible) return;
    if (!notificationsEnabled) return;
    if (isOtherModalOpen()) return;
    if (!isUserActive()) return;
    if (Date.now() - lastNotificationTime.current < timingSettings.minIntervalMs) return;

    const card = await fetchRandomCard();
    if (card) {
      setCurrentCard(card);
      setIsVisible(true);
      const now = Date.now();
      lastNotificationTime.current = now;
      setLastNotificationTime(now); // Persist to localStorage
    }
  }, [currentUser, location.pathname, isVisible, notificationsEnabled, isOtherModalOpen, isUserActive, fetchRandomCard, timingSettings.minIntervalMs]);

  // Set up interval to check for notifications
  useEffect(() => {
    // Initial delay before first possible notification (uses timing settings)
    const initialDelay = setTimeout(() => {
      tryShowNotification();
      
      // Then check periodically
      checkIntervalRef.current = setInterval(() => {
        tryShowNotification();
      }, ACTIVITY_CHECK_INTERVAL_MS);
    }, timingSettings.initialDelayMs);

    return () => {
      clearTimeout(initialDelay);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [tryShowNotification, timingSettings.initialDelayMs]);

  // Handle dismiss
  const handleDismiss = () => {
    setIsVisible(false);
    setCurrentCard(null);
  };

  // Handle turn off notifications
  const handleTurnOff = async () => {
    try {
      const token = await getToken();
      await axios.put(
        `${BACKEND_URL}/api/notification-cards/settings`,
        { notificationsEnabled: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationsEnabled(false);
      setShowTurnOffMessage(true);
      handleDismiss();
      
      // Auto-dismiss the turn off message after 5 seconds
      if (turnOffMessageTimeoutRef.current) {
        clearTimeout(turnOffMessageTimeoutRef.current);
      }
      turnOffMessageTimeoutRef.current = setTimeout(() => {
        setShowTurnOffMessage(false);
      }, 5000);
    } catch (error) {
      console.error('Error turning off notifications:', error);
    }
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (turnOffMessageTimeoutRef.current) {
        clearTimeout(turnOffMessageTimeoutRef.current);
      }
    };
  }, []);
  
  // Dismiss the turn off message
  const dismissTurnOffMessage = () => {
    setShowTurnOffMessage(false);
    if (turnOffMessageTimeoutRef.current) {
      clearTimeout(turnOffMessageTimeoutRef.current);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  // Focus trap
  const modalRef = useRef(null);
  useEffect(() => {
    if (isVisible && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isVisible]);

  return (
    <>
      {children}
      
      {/* Notification Modal */}
      {isVisible && currentCard && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: currentCard.backgroundColor || 'rgba(0, 0, 0, 0.5)' }}
          onClick={handleDismiss}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notification-title"
        >
          <div
            ref={modalRef}
            className="relative w-full max-w-xl md:max-w-3xl rounded-xl shadow-2xl overflow-hidden"
            style={{ 
              backgroundColor: currentCard.cardBackgroundColor || '#ffffff',
              maxHeight: '85vh',
              minWidth: '340px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
              onClick={handleDismiss}
              aria-label="Close notification"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-gray-200" />
            </button>

            {/* Content Area */}
            <div className="relative min-h-[450px] md:min-h-[550px] pt-6 pb-28 px-6 md:px-10">
              {currentCard.contentBlocks?.map((block) => (
                <div
                  key={block.id}
                  className="absolute"
                  style={{
                    left: `${block.x}%`,
                    top: `${block.y}%`,
                    width: `${block.width}%`,
                    height: block.height ? `${block.height}%` : 'auto',
                  }}
                >
                  {block.type === 'text' ? (
                    <div
                      className="text-scale-responsive"
                      style={{
                        fontFamily: block.fontFamily || 'Manrope',
                        '--base-font-size': `${block.fontSize || 16}px`,
                        fontWeight: block.fontWeight || 'normal',
                        fontStyle: block.fontStyle || 'normal',
                        textAlign: block.textAlign || 'left',
                        color: block.color || '#1f2937',
                        lineHeight: 1.4,
                      }}
                    >
                      {block.content}
                    </div>
                  ) : (
                    block.content && (
                      <img
                        src={block.content}
                        alt=""
                        className="w-full h-full object-contain"
                      />
                    )
                  )}
                </div>
              ))}
            </div>

            {/* Fixed Bottom Section */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white via-white to-transparent dark:from-gray-800 dark:via-gray-800">
              <Button
                className="w-full py-4 text-lg font-medium"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
                }}
                onClick={handleDismiss}
              >
                <span className="text-white font-semibold">I got it</span>
              </Button>
              <button
                className="w-full mt-3 text-sm text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 transition-colors"
                onClick={handleTurnOff}
              >
                Turn these notifications off
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Turn off confirmation message - auto-dismisses after 5 seconds */}
      {showTurnOffMessage && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-gray-800 text-white p-4 rounded-lg shadow-lg z-50 animate-in slide-in-from-bottom duration-300">
          <div className="flex justify-between items-start">
            <p className="text-sm flex-1 pr-2">
              Notifications turned off. You can turn them back on anytime from your Profile.
            </p>
            <button
              onClick={dismissTurnOffMessage}
              className="text-gray-400 hover:text-white transition-colors p-1 -mt-1 -mr-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="text-blue-400 text-sm hover:underline"
              onClick={() => {
                dismissTurnOffMessage();
                navigate('/profile');
              }}
            >
              Go to Profile
            </button>
            <button
              className="text-gray-400 text-sm hover:text-gray-200"
              onClick={dismissTurnOffMessage}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
